import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Check } from "lucide-react";

const LINES = [
  { text: "$ teckube deploy --env production", tone: "text-ink" },
  { text: "✓ cloud infrastructure provisioned", tone: "text-soot" },
  { text: "✓ containers rolled out to kubernetes", tone: "text-soot" },
  { text: "✓ release pipeline automated", tone: "text-soot" },
  { text: "→ easier to operate, more consistent releases", tone: "text-primary" },
];

const LINE_STAGGER = 0.85;
const CYCLE_MS = 8600;

const TerminalCard = () => {
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setCycle((c) => c + 1), CYCLE_MS);
    return () => clearTimeout(t);
  }, [cycle]);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 120, damping: 18 });
  const sy = useSpring(my, { stiffness: 120, damping: 18 });
  const rotateX = useTransform(sy, [0, 1], [7, -7]);
  const rotateY = useTransform(sx, [0, 1], [-9, 9]);

  return (
    <div
      className="relative"
      style={{ perspective: 1200 }}
      data-testid="hero-terminal-card"
    >
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          mx.set((e.clientX - r.left) / r.width);
          my.set((e.clientY - r.top) / r.height);
        }}
        onMouseLeave={() => {
          mx.set(0.5);
          my.set(0.5);
        }}
        initial={{ opacity: 0, y: 48 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white/85 shadow-[0_40px_90px_-36px_rgba(15,23,42,0.4)] backdrop-blur-xl"
      >
        <div className="flex items-center gap-2 border-b border-slate-100 px-6 py-4">
          <span className="h-3 w-3 rounded-full bg-[#FF5F57]" />
          <span className="h-3 w-3 rounded-full bg-[#FEBC2E]" />
          <span className="h-3 w-3 rounded-full bg-[#28C840]" />
          <span className="ml-3 font-mono text-xs text-slate-400">teckube — delivery</span>
          <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300">
            run #{cycle + 1}
          </span>
        </div>

        <div className="min-h-[190px] space-y-3 px-6 py-6 font-mono text-[13px] leading-relaxed md:text-sm">
          {LINES.map((line, i) => (
            <motion.p
              key={`${cycle}-${line.text}`}
              className={line.tone}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + i * LINE_STAGGER, duration: 0.4 }}
            >
              {line.text}
            </motion.p>
          ))}
          <div className="flex items-center gap-2 pt-1">
            <Check className="h-4 w-4 text-primary" />
            <motion.span
              className="h-4 w-[2px] bg-primary"
              animate={{ opacity: [1, 0, 1] }}
              transition={{ repeat: Infinity, duration: 1.1 }}
            />
          </div>
        </div>

        <div className="h-1 w-full bg-slate-100">
          <motion.div
            key={`progress-${cycle}`}
            className="h-full bg-primary/70"
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: CYCLE_MS / 1000, ease: "linear" }}
          />
        </div>
      </motion.div>

      <span
        className="floaty absolute -right-4 -top-6 rounded-full border border-slate-200 bg-white px-4 py-2 font-display text-xs font-semibold text-ink shadow-lg md:-right-8"
        style={{ transform: "translateZ(60px)" }}
        data-testid="hero-chip-kubernetes"
      >
        Kubernetes
      </span>
      <span
        className="floaty-slow absolute -bottom-6 -left-4 rounded-full border border-slate-200 bg-white px-4 py-2 font-display text-xs font-semibold text-ink shadow-lg md:-left-10"
        style={{ transform: "translateZ(45px)" }}
        data-testid="hero-chip-openshift"
      >
        Red Hat OpenShift
      </span>
    </div>
  );
};

export default TerminalCard;
